import { IconButton, Tooltip } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";

import { mergeClasses, SimpleMenuAction } from "../";

const useStyles = makeStyles(
    () =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
                marginTop: 24,
            },
            label: {
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.54)",
            },
            header: {
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-end",
            },
            actions: {
                marginLeft: 16,
            },
            content: {
                flexGrow: 1,
            },
            contentOffsetForLabel: {
                marginTop: 0,
            },
            icon_svg: {},
            icon_button: {
                padding: 8,
                marginBottom: -6,
                marginLeft: 8,
            },
            button: {
                marginLeft: 16,
            },
            button_label: {
                fontSize: "0.75em",
                fontWeight: "normal",
                fontStyle: "normal",
                color: "rgba(0, 0, 0, 0.54)",
                marginLeft: 8,
                // eslint-disable-next-line quotes
                fontFamily: '"hkgrotesk_regular"',
            },
        }),
    { name: "DataSection" }
);

export interface DataSectionProps {
    classes?: Partial<ReturnType<typeof useStyles>>;
    baseId?: string;
    children?: React.ReactNode;
    className?: string;
    contentClassName?: string;
    label?: string;
    actions?: SimpleMenuAction[];
    style?: React.CSSProperties;
}

const IconButtonAction = React.forwardRef(
    (
        props: { action: SimpleMenuAction; classes?: any; baseId?: string },
        ref?: any
    ) => {
        const { action, baseId, classes: classesProp, ...rest } = props;
        const classes = mergeClasses(useStyles({}), classesProp);
        const id = !!action.id ? `${baseId}${action.id}` : undefined;

        const renderIcon = (icon: JSX.Element) =>
            React.cloneElement(icon, {
                className: classes.icon_svg,
            });

        return (
            <IconButton
                id={id}
                {...rest}
                ref={ref}
                className={classes.icon_button}
                color="primary"
                onClick={action.action}
            >
                {renderIcon(action.icon)}
            </IconButton>
        );
    }
);

export const DataSection = React.forwardRef(
    (props: DataSectionProps, ref: any) => {
        const {
            baseId,
            actions,
            children,
            label,
            className: classNameProp,
            contentClassName: contentClassNameProp,
            style,
        } = props;
        const classes = mergeClasses(useStyles({}), props.classes);

        const rootClasses = classnames(classes.root, classNameProp);
        const contentClasses = classnames(
            classes.content,
            contentClassNameProp,
            label && classes.contentOffsetForLabel
        );

        return (
            <div className={rootClasses} style={style}>
                {label || actions ? (
                    <div className={classes.header}>
                        {label && (
                            <span className={classes.label}>{label}</span>
                        )}

                        {actions && actions.length > 0 ? (
                            <div className={classes.actions}>
                                {actions.map(a => (
                                    <Tooltip
                                        placement="bottom-start"
                                        key={a.label}
                                        title={a.tooltip}
                                    >
                                        <IconButtonAction
                                            baseId={baseId}
                                            action={a}
                                            classes={classes}
                                        />
                                    </Tooltip>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div className={contentClasses}>{children}</div>
            </div>
        );
    }
);
